import { useState, useEffect, useCallback, useRef, useContext, createContext } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import { toDateOnly, fromTimeOnly, deleteItem } from '../api/figApi';
import {
  getFoodLogForRange,
  totalNutrients,
  estimatedCalories,
  sortByTime,
  shiftDay,
  formatDayHeader,
  localDateFromDateOnly,
  NUTRIENTS,
  DETAIL_NUTRIENTS,
} from './foodLog';
import {
  summarizeWindow,
  groupRowsByDay,
  weeklyRateLb,
  predictedChangeLb,
  observedChangeLb,
  MIN_DAYS_FOR_RATE,
} from './weekWindow';
import { proteinRunRate, nutrientRunRate, PROTEIN_TARGET_G_PER_DAY, PROTEIN_WINDOW_DAYS } from './proteinRunRate';
import { NUTRIENT_TARGETS, CALORIE_TARGET_CAL, nutrientStatus, statusColor } from './nutritionTargets';
import FoodPopover, { IS_TOUCH } from './FoodPopover';
import {
  REVEAL_TOTAL_MS, SEGMENTS, segmentProgress,
  mineralRevealDelayMs, mineralCountSegment, rowRevealDelayMs,
} from './revealTimeline';
// FIRST src/food -> src/fitness import. Food.css:4-7 notes that this module
// shares nothing with Fitness, and that still holds for styling; this is a data
// crossing, not a visual one. SwimmingWorkouts and FitnessWeighIns are defined
// in fitnessApi's LIST_DEFINITIONS and nowhere else, so reading them through
// figApi would mean either duplicating those definitions or depending on the
// lists never needing to be created. The module that owns them does the read.
//
// TODO: fitnessApi.getItems is $top=500 with no @odata.nextLink paging
// (fitnessApi.js:353), unlike figApi's. Daily weigh-ins reach that ceiling in
// roughly 16 months (docs/BODY_Swimming_Notes_v1.md:263). Not this change's
// problem to fix, but it is this change's problem eventually.
import { getItems as getFitnessItems } from '../fitness/fitnessApi';
import { computeBurn, runwayPositions } from './burn';
import { formatDeficitHeadline } from './burnLabel';
import AddFoodSheet from './AddFoodSheet';
import './Food.css';

// Food — Today view, read path only. Phase 4.
//
// Numbers, not gauges: no rings, no bars, no targets. The day's totals are
// stated plainly and an undisclosed nutrient is stated as undisclosed rather
// than as zero, because a day that reads 0 mg magnesium and a day whose
// sources never mentioned magnesium are different days.
//
// The day is ONE chronological ledger, not four meal buckets. MealSlot is not
// read here. Buckets asked the log a question it could not answer — a 10pm
// bowl of cereal is a Snack or a Dinner depending on nothing the row records —
// and the answer was a fifth "Unassigned" heading that existed only to catch
// rows the other four could not classify. Time is a fact about the entry;
// which meal it belonged to is an interpretation of it.
//
// Times render exactly as stored: '18:15', 24-hour, no conversion and no
// AM/PM. The column is local wall clock (see the guard in figApi) — there is no
// zone to convert from and nothing to reformat toward.
//
// Everything about dates, times and sums lives in foodLog.js, and everything
// about turning a catalog entry into a row lives in foodEntry.js. This file
// renders, and owns the two things that change what is on screen: the day, and
// a reload counter that the write paths bump.

// The six front-page tiles, display order. Each entry IS its
// nutritionTargets record — key doubles as the Food_Log column name,
// target/unit/direction ride along for the tap-open detail. The numbers are
// never restated here. Past four the strip scrolls (Food.css), so the two
// micronutrient tiles ride the same row without layout changes.
const MINERALS = [
  NUTRIENT_TARGETS.Sodium,
  NUTRIENT_TARGETS.Potassium,
  NUTRIENT_TARGETS.Magnesium,
  NUTRIENT_TARGETS.Calcium,
  NUTRIENT_TARGETS.IodineMcg,
  NUTRIENT_TARGETS.RetinolMcg,
];

// One number, or an em dash when nothing disclosed it. The dash carries its own
// class and title so it can never be misread as a zero — that ambiguity is the
// one thing this view must not introduce.
//
// `undisclosed` is how many of the day's rows withheld this nutrient, and it
// splits the reading into three states the eye can tell apart:
//
//   nothing disclosed it        -> em dash, no number at all
//   some disclosed it, some not -> the number with a superscript plus: 99⁺
//   everything disclosed it     -> the number, plain
//
// The plus is the caveat, attached to the one number it qualifies. A partial
// total is a real floor — at least this much — and printing it bare would
// assert a precision the day does not have.
// `count`, when given, is a reveal segment: the numeric part counts up through
// it on a day-landing (CountUp below) and renders plain everywhere else. The
// dash, the superscript and the unit never animate — only digits are measured.
function Amount({ value, unit, undisclosed = 0, count = null }) {
  if (value === undefined || value === null) {
    return (
      <span className="Food-undisclosed" title="Not disclosed">&mdash;</span>
    );
  }
  return (
    <>
      {count !== null
        ? <CountUp value={value} segment={count} />
        : Math.round(value).toLocaleString('en-US')}
      {undisclosed > 0 && (
        <sup
          className="Food-partial"
          title={`At least this much — ${undisclosed} ${undisclosed === 1 ? 'entry' : 'entries'} did not disclose it`}
        >
          +
        </sup>
      )}
      {unit ? <span className="Food-unit"> {unit}</span> : null}
    </>
  );
}

// ── The reveal ───────────────────────────────────────────────────────────────
//
// When a day LANDS — first mount, or navigation via the ‹ › arrows — its
// numbers are measured into place: the ring sweeps, digits count, bars fill,
// tiles rise, in the one choreography revealTimeline.js tabulates. A refetch
// is not a landing: saving a food remounts this content with a stale clock,
// every progress reads 1, and nothing moves. Everything here is decoration
// over a tree that always renders its final values.
//
// The context carries the day's clock. The default is ancient, so anything
// rendered outside a provider — or after the sequence — is simply settled.
const RevealContext = createContext({ startTime: -Infinity, reduced: true });

// Eased progress through one timeline segment, re-rendering per frame while
// it runs. Settled mounts and prefers-reduced-motion return 1 immediately and
// never schedule a frame; a mount mid-sequence (the burn card landing during
// the reveal) starts from the progress the shared clock says it should have.
function useRevealProgress(segment) {
  const { startTime, reduced } = useContext(RevealContext);
  const { start, duration } = segment;
  const animate = !reduced && performance.now() - startTime < REVEAL_TOTAL_MS;
  const [p, setP] = useState(() =>
    animate ? segmentProgress(performance.now() - startTime, { start, duration }) : 1
  );
  useEffect(() => {
    if (!animate) return undefined;
    let raf = requestAnimationFrame(function tick() {
      const elapsed = performance.now() - startTime;
      setP(segmentProgress(elapsed, { start, duration }));
      if (elapsed < start + duration) raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [animate, startTime, start, duration]);
  return p;
}

// Read at each landing rather than once at load — the OS setting can change
// mid-session and the next day paged to should honour it.
function prefersReducedMotion() {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// A number that counts up during the reveal. The counting span is
// aria-hidden for its whole life and a visually-hidden twin always holds the
// final value, so assistive tech never hears a mid-count number — the DOM's
// readable value is the real one throughout. The count rides the segment's
// eased progress: decelerating, never past the target.
function CountUp({ value, segment }) {
  const p = useRevealProgress(segment);
  const final = Math.round(value).toLocaleString('en-US');
  return (
    <>
      <span aria-hidden="true">{p === 1 ? final : Math.round(value * p).toLocaleString('en-US')}</span>
      <span className="Food-sr-only">{final}</span>
    </>
  );
}

function formatQuantity(entry) {
  const unit = entry.Unit || '';
  if (entry.Quantity === undefined || entry.Quantity === null) return unit;
  return `${entry.Quantity} ${unit}`.trim();
}

// A weigh-in older than this reads as a stale basis for today's burn. Two weeks
// of unrecorded drift moves resting burn by more than the rounding hides.
const STALE_WEIGHIN_DAYS = 14;

// The burn number is an estimate and is rounded to say so. Printing 2,287 would
// assert a precision that a hardcoded height, a hardcoded age and a MET table
// cannot support. Applied to the point value and to both ends of the display
// range, so every burn figure on screen shares one precision.
const BURN_ROUNDING = 50;

// 'YYYY-MM-DD' -> 'M/D'. No year: the basis date is always near the day being
// viewed, and when it is not, the stale styling is what says so.
function formatBasisDate(dayKey) {
  const [, m, d] = dayKey.split('-');
  return `${Number(m)}/${Number(d)}`;
}

function daysBetween(fromKey, toKey) {
  const from = localDateFromDateOnly(fromKey);
  const to = localDateFromDateOnly(toKey);
  return Math.round((to - from) / 86400000);
}

// Intake on a runway to burn, with a surplus region past it. One meaningful
// boundary — that day's burn — and nothing on the track names zones, ranks
// positions, or implies further left is better.
//
// Three distinct absences, and the bar says which one it is rather than
// collapsing them into a single blank:
//
//   burn === null        -> no weigh-in on or before this day. No weight, no
//                           burn, no bar. Not a default weight.
//   intakeCal === null   -> no row disclosed calories (a day with no rows now
//                           lands here too — see the intakeCal note below).
//                           The burn range still prints and the track renders
//                           EMPTY with the burn tick shown; the headline is an
//                           em dash. An empty track is honest here because the
//                           segments are fills, not a marker — no fill is
//                           "nothing placed", where the old marker-at-zero
//                           read as "zero eaten".
//   incomplete           -> the burn is a floor. Marked with the same superscript
//                           plus every other partial total on this screen uses.
//
// Rendered by two mutually exclusive call sites — inside the populated-day
// block, and again on an empty day. Never both at once.
// The one rounding every burn figure on this screen shares.
function roundedBurnCal(burn) {
  return Math.round(burn.burnCal / BURN_ROUNDING) * BURN_ROUNDING;
}


// SVG geometry for the hero ring — one circle, so one circumference.
const RING_R = 54;
const RING_C = 2 * Math.PI * RING_R;

// The hero ring: filled to intake / burn — the SAME fraction, numerator and
// denominator as the bar, so the two can never disagree. Unfilled when either
// side is unknown; capped at full when intake passes burn. Inline SVG, no
// library; the fill animates in via a dashoffset keyframe that
// prefers-reduced-motion switches off.
// Also the calorie popover's trigger. A div with role="button" rather than a
// <button>, because the children include <p> elements and buttons take
// phrasing content only; Enter/Space are wired by hand for the same reason.
// `hover` is the desktop-only mouseenter/mouseleave pair (empty on touch).
function FoodRing({ burn, intakeCal, ringColor, anchorRef, onToggle, popoverOpen, hover, children }) {
  const fraction = burn === null || intakeCal === null
    ? 0
    : Math.min(1, Math.max(0, intakeCal / roundedBurnCal(burn)));

  // The arc sweeps 0 -> fraction on a day-landing; settled mounts render the
  // final arc because p is 1 before the first paint. The kcal digits inside
  // ride the SAME segment, so they land the frame the arc stops.
  const reveal = useRevealProgress(SEGMENTS.ring);

  return (
    <div
      className="Food-ring"
      role="button"
      tabIndex={0}
      data-food-pop=""
      ref={anchorRef}
      onClick={onToggle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
      }}
      aria-expanded={popoverOpen}
      aria-label="Calorie detail"
      {...hover}
    >
      <svg className="Food-ring-svg" viewBox="0 0 120 120" aria-hidden="true">
        <circle className="Food-ring-track" cx="60" cy="60" r={RING_R} />
        <circle
          className="Food-ring-fill"
          cx="60" cy="60" r={RING_R}
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - fraction * reveal)}
          style={{ stroke: ringColor }}
        />
      </svg>
      <div className="Food-ring-content">{children}</div>
    </div>
  );
}

// Centre of the hero row: the headline, then the day's even point. The
// headline strings are exactly formatDeficitHeadline's three shapes — no
// praise, no verdict, no projection; the app reports position and has no
// opinions about it.
function HeroCentre({ burn, intakeCal }) {
  if (burn === null) {
    return (
      <p className="Food-burn-deficit">
        <span className="Food-undisclosed" title="No weigh-in recorded on or before this day">&mdash;</span>
      </p>
    );
  }
  const rounded = roundedBurnCal(burn);
  return (
    <>
      <p className="Food-burn-deficit">
        {intakeCal === null ? (
          <span className="Food-undisclosed" title="No entry disclosed calories">&mdash;</span>
        ) : (
          formatDeficitHeadline(rounded - intakeCal)
        )}
      </p>
      <p className="Food-hero-even">{rounded.toLocaleString('en-US')} even</p>
    </>
  );
}

// The subline under the burn range: what the day's activity actually was.
// Four states — swim, walk, both (joined with the Title convention's '·'),
// or the honest absence. A walk names its distance whenever any of the day's
// rows carries one, and falls back to total minutes only when none does — a
// duration-only walk is still a walk. Both figures come from computeBurn,
// which sums them over whatever was LOGGED, estimable or not.
function activityLabel(burn) {
  const parts = [];
  if (burn.swimMeters > 0) {
    parts.push(`${burn.swimMeters.toLocaleString('en-US')} m swim`);
  }
  if (burn.walkMiles > 0) {
    parts.push(`${burn.walkMiles.toLocaleString('en-US', { maximumFractionDigits: 1 })} mi walk`);
  } else if (burn.walkMinutes > 0) {
    parts.push(`${Math.round(burn.walkMinutes)}m walk`);
  }
  return parts.length === 0 ? 'no activity logged' : parts.join(' · ');
}

// The burn range and its activity subline, in the hero row's right tile.
//
// No "est." on the burn: on this screen est. already means the share of
// INTAKE that was estimated, and the range IS the burn's uncertainty, stated
// as width instead of a suffix.
function BurnTile({ burn }) {
  const low = burn === null ? null : Math.round(burn.burnLowCal / BURN_ROUNDING) * BURN_ROUNDING;
  const high = burn === null ? null : Math.round(burn.burnHighCal / BURN_ROUNDING) * BURN_ROUNDING;

  return (
    <div className="Food-hero-tile">
      {burn === null ? (
        <>
          <p className="Food-inout-burn">
            <span className="Food-undisclosed" title="No weigh-in recorded on or before this day">
              &mdash;
            </span>
            <span className="Food-burn-label"> burned</span>
          </p>
          <p className="Food-burn-swim">no weigh-in recorded</p>
        </>
      ) : (
        <>
          <p className="Food-inout-burn">
            <span className="Food-burn-value">
              {low.toLocaleString('en-US')}&ndash;{high.toLocaleString('en-US')}
              {burn.incomplete && (
                <sup
                  className="Food-partial"
                  title="At least this much — an activity on this day was logged without enough detail to estimate fully"
                >
                  +
                </sup>
              )}
            </span>
            <span className="Food-burn-label"> burned</span>
          </p>
          <p className="Food-burn-swim">{activityLabel(burn)}</p>
        </>
      )}
    </div>
  );
}

function BurnBar({ burn, intakeCal }) {
  // The sweep: one progress value drives the clip edge, the pill riding it,
  // and the tick's cue. Hook first — it must run on the null-burn renders
  // too, so the hook order cannot change when the burn lands mid-reveal.
  const sweep = useRevealProgress(SEGMENTS.bar);

  // The no-weigh-in state lives on the hero row now; with no burn there is no
  // scale, so there is no bar.
  if (burn === null) return null;

  // The rounded point value is the basis for the bar's geometry, rounded to 50
  // so no number on this bar carries more precision than the estimate has.
  // The headline lives in HeroCentre, the display range in BurnTile — all
  // three read the same roundedBurnCal.
  const rounded = roundedBurnCal(burn);
  const deficit = intakeCal === null ? null : rounded - intakeCal;
  const pos = runwayPositions(intakeCal === null ? 0 : intakeCal, rounded);
  const over = deficit !== null && pos.overPct > pos.burnPct;

  // The edge crosses the whole track, so the tick is always passed; on an
  // intake-less day there is no fill and the tick's cue is the segment
  // simply starting. Settled mounts read 1 before first paint.
  const sweepPct = sweep * 100;
  const tickShown = deficit === null ? sweep > 0 : sweepPct >= pos.burnPct;
  const pillShown = sweep > 0;

  return (
    <div className={`Food-burn${tickShown ? '' : ' Food-burn--premark'}${pillShown ? '' : ' Food-burn--prefill'}`}>
      <div className="Food-burn-plot">
      <div
        className="Food-burn-track"
        role="img"
        aria-label={
          deficit === null
            ? `intake undisclosed — ${rounded.toLocaleString('en-US')} burned`
            : `${Math.round(intakeCal).toLocaleString('en-US')} eaten of ` +
              `${rounded.toLocaleString('en-US')} burned — ${formatDeficitHeadline(deficit)}`
        }
      >
        {/* Four flat segments, painted only when intake is known; the neutral
            burn tick always. Solid green is what was eaten (stops at burn),
            pale green the runway left, solid red the surplus actually eaten,
            pale red the surplus region not reached. */}
        {deficit !== null && (
          /* The segs wrapper exists for the reveal alone: inset 0 keeps every
             child's left/width reading against the same box as before, and
             the clip edge wipes the whole painted strip left to right. At
             sweep 1 the clip is a no-op. */
          <span
            className="Food-burn-segs"
            style={{ clipPath: `inset(0 ${100 - sweepPct}% 0 0)` }}
          >
            {pos.eatenPct > 0 && (
              <span
                className="Food-burn-seg Food-burn-seg--eaten"
                style={{ left: 0, width: `${pos.eatenPct}%` }}
              />
            )}
            {!over && pos.burnPct > pos.eatenPct && (
              <span
                className="Food-burn-seg Food-burn-seg--runway"
                style={{ left: `${pos.eatenPct}%`, width: `${pos.burnPct - pos.eatenPct}%` }}
              />
            )}
            {over && (
              <span
                className="Food-burn-seg Food-burn-seg--overrun"
                style={{ left: `${pos.burnPct}%`, width: `${pos.overPct - pos.burnPct}%` }}
              />
            )}
            <span
              className="Food-burn-seg Food-burn-seg--surplus"
              style={{
                left: `${Math.max(pos.burnPct, pos.overPct)}%`,
                width: `${100 - Math.max(pos.burnPct, pos.overPct)}%`,
              }}
            />
          </span>
        )}
        <span className="Food-burn-mark" style={{ left: `${pos.burnPct}%` }} />
      </div>

      {/* The current position, carried as a pill riding the bar: its left
          tracks the sweep edge until the edge passes its resting place, where
          it detaches and stays. Settled and reduced-motion mounts read
          sweep 1 and render it in place. Only when there is an intake to
          place — an empty track carries no pill. */}
      {deficit !== null && (
        <span className="Food-burn-pill" style={{ left: `${Math.min(pos.overPct, sweepPct)}%` }}>
          {Math.round(intakeCal).toLocaleString('en-US')}
        </span>
      )}
      </div>

      {/* The tick's name. Same left as the tick — both read pos.burnPct — so
          the pair move as one object when the day's burn moves. The row keeps
          its height whether or not a label is in it, so the ledger below
          never shifts. */}
      <div className="Food-burn-marklabel-row" aria-hidden="true">
        <span className="Food-burn-marklabel" style={{ left: `${pos.burnPct}%` }}>
          {rounded.toLocaleString('en-US')}
          <span className="Food-burn-marklabel-sub">today&rsquo;s maintenance</span>
        </span>
      </div>
    </div>
  );
}

// The weight the day's burn rests on, below the ledger. "this morning" when
// the reading in force is today's; otherwise the actual date, muted when it is
// old enough that drift outruns the rounding. Reads only what computeBurn
// already loaded — no second fetch. The "Past 7 days" button is a wired-to-
// nothing seam for the rolling view.
function WeightRow({ burn, dayKey, onOpenWeek }) {
  const today = toDateOnly(new Date());
  const staleDays = burn === null ? 0 : daysBetween(burn.weighInDayKey, dayKey);
  const stale = staleDays > STALE_WEIGHIN_DAYS;

  return (
    <div className="Food-weightrow">
      <span className="Food-weightrow-reading">
        {burn === null ? (
          <span className="Food-undisclosed" title="No weigh-in recorded">&mdash;</span>
        ) : (
          <>
            {burn.weightLbs.toLocaleString('en-US')} lb
            {' · '}
            <span
              className={stale ? 'Food-burn-date--stale' : undefined}
              title={stale ? `Weighed ${staleDays} days before this day` : undefined}
            >
              {burn.weighInDayKey === today ? 'this morning' : formatBasisDate(burn.weighInDayKey)}
            </span>
          </>
        )}
      </span>
      <button type="button" className="Food-weightrow-week" onClick={onOpenWeek}>
        Past 7 days
      </button>
    </div>
  );
}

// Protein over the trailing seven days ending on the VIEWED day. The window
// follows the ‹ › nav — page back to Aug 1 and it reads Jul 26–Aug 1 — unlike
// WeekSheet's window, which always ends today. That divergence is deliberate.
//
// Three states by loggedDays, all distinguishable:
//
//   0     -> the em dash and "no days logged". Never a 0 — an unlogged week
//            is not a zero-protein week.
//   1..3  -> the number and its caveat, with the percentage and bar
//            SUPPRESSED. A run rate off fewer than MIN_DAYS_FOR_RATE days is
//            not a rate — the same threshold, and the same constant, as the
//            week overlay's rate line.
//   4+    -> the full card: number, caveat, target, bar, percentage.
//
// The "N of 7" caveat sits beside the number it qualifies, same principle as
// the superscript plus — which the number also carries when a logged row
// withheld protein, because that makes the rate a floor.
//
// Null data renders nothing: the window is still loading or its read failed,
// and neither of those is "no days logged".
function ProteinRunRateCard({ data }) {
  if (data === null) return null;
  const { runRate, loggedDays, windowDays, undisclosed } = data;
  const full = loggedDays >= MIN_DAYS_FOR_RATE;
  const pctOfTarget = full ? Math.round((runRate / PROTEIN_TARGET_G_PER_DAY) * 100) : null;

  return (
    <div className="Food-card Food-protein">
      <p className="Food-protein-heading">Protein run rate</p>
      <div className="Food-protein-main">
        <p className="Food-protein-rate">
          {loggedDays === 0 ? (
            <span className="Food-undisclosed" title="No days logged in this window">&mdash;</span>
          ) : (
            <>
              <span className="Food-protein-rate-value">
                {Math.round(runRate).toLocaleString('en-US')}
                {undisclosed > 0 && (
                  <sup
                    className="Food-partial"
                    title={`At least this much — ${undisclosed} logged ${undisclosed === 1 ? 'entry' : 'entries'} did not disclose protein`}
                  >
                    +
                  </sup>
                )}
              </span>
              <span className="Food-unit"> g/day</span>
            </>
          )}
        </p>
        <p className="Food-protein-logged">
          {loggedDays === 0 ? 'no days logged' : `${loggedDays} of ${windowDays} days logged`}
        </p>
      </div>
      {full && (
        <div className="Food-protein-target-row">
          <span className="Food-protein-target">target {PROTEIN_TARGET_G_PER_DAY}</span>
          <span
            className="Food-protein-bar"
            role="img"
            aria-label={`${pctOfTarget}% of ${PROTEIN_TARGET_G_PER_DAY} g/day target`}
          >
            <span
              className="Food-protein-bar-fill"
              style={{ width: `${Math.min(100, pctOfTarget)}%` }}
            />
          </span>
          <span className="Food-protein-pct">{pctOfTarget}%</span>
        </div>
      )}
    </div>
  );
}

// One mineral's detail: the day's total against its target, a thin bar, and
// the disclosure caveats spelled out in words. The bar goes muted, not
// coloured, on 'incomplete' — a floor gets stated, never judged. The value
// half of the reading is Amount (same dash, same superscript plus as
// everywhere else); the target half is hand-rolled because a target is a
// config number, not a disclosed measurement.
function MineralPopover({ targetDef, total, dayKeys, rowsByDay }) {
  const { key, label, target, unit, direction } = targetDef;
  const { state, ratio } = nutrientStatus({
    value: total.value, undisclosed: total.undisclosed, target, direction,
  });
  const color = statusColor(state);
  const lower = label.toLowerCase();
  const pct = ratio === null ? 0 : Math.min(100, Math.round(ratio * 100));
  // Trailing-7-day rate off the window rows the range effect keeps. Null
  // while that fetch is in flight or failed — rendered as the same dash a
  // zero-logged-days window gets.
  const windowRate = dayKeys && rowsByDay
    ? nutrientRunRate({ dayKeys, rowsByDay, key })
    : null;
  return (
    <>
      <p className="Food-popover-title">{label}</p>
      <p className="Food-popover-reading">
        <Amount value={total.value} undisclosed={total.undisclosed} unit={unit} />
        {' of '}
        {target.toLocaleString('en-US')}
        <span className="Food-unit"> {unit}</span>
      </p>
      <p className="Food-popover-sub">{direction === 'ceiling' ? 'limit' : 'target'}</p>
      <span
        className="Food-popover-bar"
        role="img"
        aria-label={total.value === null ? `no ${lower} disclosed` : `${pct}% of ${lower} ${direction === 'ceiling' ? 'limit' : 'target'}`}
      >
        <span
          className="Food-popover-bar-fill"
          style={{ width: `${pct}%`, background: color ?? 'var(--dh-text-muted)' }}
        />
      </span>
      {/* The run rate, in the slot CaloriePopover keeps for its delta line —
          after the bar, before the caveat notes. Same language as the
          protein card: a per-day figure, the day count it rests on, and the
          superscript plus when withheld rows make the rate a floor. */}
      <p className="Food-popover-delta">
        {windowRate === null || windowRate.loggedDays === 0 ? (
          <span className="Food-undisclosed" title="No days logged in this window">&mdash;</span>
        ) : (
          <>
            {Math.round(windowRate.runRate).toLocaleString('en-US')}
            {windowRate.undisclosed > 0 && (
              <sup
                className="Food-partial"
                title={`At least this much — ${windowRate.undisclosed} logged ${windowRate.undisclosed === 1 ? 'entry' : 'entries'} did not disclose ${lower}`}
              >
                +
              </sup>
            )}
            <span className="Food-unit"> {unit}/day</span>
            {' · '}
            {windowRate.loggedDays} of {windowRate.windowDays} days logged
          </>
        )}
      </p>
      {state === 'incomplete' && (
        <p className="Food-popover-note">
          {total.undisclosed} of today&rsquo;s entries didn&rsquo;t disclose {lower}. Actual intake is higher.
        </p>
      )}
      {state === 'unknown' && (
        <p className="Food-popover-note">No entries today disclosed {lower}.</p>
      )}
    </>
  );
}

// The calorie ring's detail: intake against the resolved target and which
// side of it the day is on. Iodine and retinol moved out to their own
// mineral tiles — this panel no longer carries an "Also tracked" block.
function CaloriePopover({ intake, target, targetLabel, state }) {
  const delta = target === null || intake.value === null ? null : target - intake.value;
  return (
    <>
      <p className="Food-popover-title">Calories</p>
      <p className="Food-popover-reading">
        <Amount value={intake.value} undisclosed={intake.undisclosed} unit="cal" />
        {' of '}
        {target === null ? (
          <span className="Food-undisclosed" title="No weigh-in recorded on or before this day">&mdash;</span>
        ) : (
          <>
            {target.toLocaleString('en-US')}
            <span className="Food-unit"> cal</span>
          </>
        )}
      </p>
      <p className="Food-popover-sub">{target === null ? 'no target — no weigh-in' : targetLabel}</p>
      {delta !== null && (
        <p className="Food-popover-delta">
          {Math.round(delta) === 0
            ? 'at target'
            : `${Math.abs(Math.round(delta)).toLocaleString('en-US')} ${delta > 0 ? 'under' : 'over'} target`}
        </p>
      )}
      {state === 'incomplete' && (
        <p className="Food-popover-note">
          {intake.undisclosed} of today&rsquo;s entries didn&rsquo;t disclose calories. Actual intake is higher.
        </p>
      )}
    </>
  );
}

// 'YYYY-MM-DD' -> 'Jul 29'. The overlay's header and nothing else.
function formatWindowDate(dayKey) {
  return localDateFromDateOnly(dayKey).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
  });
}

const DOW_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Signed pounds, one decimal: '-0.9 lb'. Both PREDICTED and OBSERVED read as
// weight change, so the gap between them is a subtraction the reader can do.
function formatLb(lb) {
  const rounded = Math.round(lb * 10) / 10;
  const shown = Object.is(rounded, -0) ? 0 : rounded;
  return `${shown > 0 ? '+' : ''}${shown.toFixed(1)} lb`;
}

// The rolling seven days ending TODAY — not the viewed day, not a calendar
// week, no Monday reset. Same overlay conventions as AddFoodSheet: Esc close,
// backdrop-click close, body scroll lock with restore, `if (!open) return
// null` after every hook.
//
// One question, answered top to bottom: the window's deficit, the same figure
// as a rate, the seven days, the two totals, and predicted-vs-observed. No
// target, no verdict, no adjective anywhere in this view.
function WeekSheet({ open, onClose, getToken, fitnessData }) {
  const [rowsByDay, setRowsByDay] = useState(null);
  const [error, setError] = useState(null);

  const endKey = toDateOnly(new Date());
  const startKey = shiftDay(endKey, -6);
  const dayKeys = [];
  for (let i = 0; i < 7; i++) dayKeys.push(shiftDay(startKey, i));

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) { if (e.key === 'Escape') onClose && onClose(); }
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // One fetch per open — the same single Food_Log pull the day view makes,
  // filtered to the range instead of one day.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setRowsByDay(null);
    setError(null);
    (async () => {
      try {
        const token = await getToken();
        const rows = await getFoodLogForRange(token, startKey, endKey);
        if (!cancelled) setRowsByDay(groupRowsByDay(rows, dayKeys));
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, getToken, startKey, endKey]);

  if (!open) return null;

  const ready = rowsByDay !== null && fitnessData !== null;
  const summary = ready
    ? summarizeWindow({ dayKeys, rowsByDay, ...fitnessData })
    : null;
  const rate = summary === null ? null : weeklyRateLb(summary.deficitTotal, summary.daysCounted);
  const predicted = summary === null ? null : predictedChangeLb(summary.deficitTotal);
  const observed = fitnessData === null ? null : observedChangeLb(fitnessData.weighIns, startKey, endKey);
  const gapPct = predicted !== null && observed !== null && Math.abs(predicted) > 0.05
    ? Math.round((Math.abs(observed - predicted) / Math.abs(predicted)) * 100)
    : null;

  // The floor superscript, worded for days rather than entries — the same
  // Food-partial vocabulary the rest of the screen uses, never a second one.
  const floorMark = summary !== null && summary.deficitIsFloor && (
    <sup
      className="Food-partial"
      title={`At least this much — ${summary.unloggedCount} of 7 days unlogged`}
    >
      +
    </sup>
  );

  return (
    <div
      className="Food-sheet-overlay"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Past 7 days"
    >
      <div className="Food-sheet Food-sheet--week" onMouseDown={e => e.stopPropagation()}>
        <div className="Food-sheet-header">
          <h2 className="Food-sheet-title">
            {formatWindowDate(startKey)}&ndash;{formatWindowDate(endKey)}
          </h2>
          <button type="button" className="Food-sheet-close" onClick={onClose}>&times;</button>
        </div>

        {error && <p className="Food-error">{error}</p>}
        {!error && !ready && <p className="Food-loading">Loading week...</p>}

        {summary !== null && (
          <>
            {/* The number. Signed wording, no verdict: a deficit and a surplus
                are both just readings. */}
            <p className="Food-week-total">
              {summary.deficitTotal === null ? (
                <span className="Food-undisclosed" title="No day in this window has both intake and a weigh-in">&mdash;</span>
              ) : (
                <>
                  <span className="Food-week-total-value">
                    {Math.round(Math.abs(summary.deficitTotal)).toLocaleString('en-US')}
                    {floorMark}
                  </span>
                  {' cal '}
                  {summary.deficitTotal >= 0 ? 'deficit' : 'surplus'}
                </>
              )}
            </p>
            {summary.deficitTotal !== null && (
              /* deficit / 3500 * 7 / days-counted, signed as the formula
                 falls: positive while under, negative while over. A unit
                 conversion, not a recommendation — and below
                 MIN_DAYS_FOR_RATE countable days, no reading at all: the
                 same discipline the observed line applies to thin weigh-ins.
                 Labelled in the PREDICTED/OBSERVED idiom so a suppressed rate
                 reads as a suppressed RATE, not a caption on the deficit. */
              <p className="Food-week-row Food-week-row--rate">
                <span className="Food-week-row-label">rate</span>
                <span className="Food-week-row-value">
                  {rate === null ? (
                    <>
                      <span className="Food-undisclosed" title="Fewer than 4 countable days in this window">&mdash;</span>
                      <span className="Food-week-row-sub"> not enough days</span>
                    </>
                  ) : (
                    <>{(Object.is(Math.round(rate * 10) / 10, -0) ? 0 : Math.round(rate * 10) / 10).toFixed(1)} lb / week</>
                  )}
                </span>
              </p>
            )}
            <p className="Food-week-logged">{summary.loggedCount} of 7 days logged</p>

            {/* Seven cells, oldest left. Unlogged renders an em dash, never a
                zero; the only colour is the deficit/surplus pair the day bar
                already uses. */}
            <div className="Food-week-days">
              {summary.days.map(d => (
                <div className="Food-week-day" key={d.dayKey}>
                  <span className="Food-week-day-dow">
                    {DOW_INITIALS[localDateFromDateOnly(d.dayKey).getDay()]}
                  </span>
                  <span
                    className={
                      d.net === null
                        ? 'Food-week-day-net'
                        : `Food-week-day-net Food-week-day-net--${d.net <= 0 ? 'deficit' : 'surplus'}`
                    }
                  >
                    {d.net === null
                      ? <span className="Food-undisclosed" title={d.state === 'unlogged' ? 'Nothing logged' : 'Incomplete day'}>&mdash;</span>
                      : Math.round(d.net).toLocaleString('en-US')}
                    {d.state === 'partial' && (
                      <sup className="Food-partial" title="A row this day did not disclose calories">+</sup>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {/* The two totals — over the SAME day set as the headline, stated
                so. Summing burn over all seven while intake covered only the
                logged days presented a pair that invited a subtraction off by
                roughly a full day's burn per missing day. Both carry the
                floor mark whenever the window has unlogged days; intake also
                carries it when a counted row withheld calories. */}
            <div className="Food-week-rows">
              <p className="Food-week-scope">
                over {summary.daysCounted} counted day{summary.daysCounted !== 1 ? 's' : ''}
              </p>
              <p className="Food-week-row">
                <span className="Food-week-row-label">intake</span>
                <span className="Food-week-row-value">
                  {summary.intakeTotal === null ? (
                    <span className="Food-undisclosed" title="No countable day in this window">&mdash;</span>
                  ) : (
                    <>
                      {Math.round(summary.intakeTotal).toLocaleString('en-US')}
                      {(summary.deficitIsFloor || summary.undisclosedRows > 0) && (
                        <sup
                          className="Food-partial"
                          title={[
                            summary.unloggedCount > 0 && `${summary.unloggedCount} of 7 days unlogged`,
                            summary.undisclosedRows > 0 && `${summary.undisclosedRows} counted ${summary.undisclosedRows === 1 ? 'row' : 'rows'} withheld calories`,
                          ].filter(Boolean).join(' · ')}
                        >
                          +
                        </sup>
                      )}
                      <span className="Food-unit"> cal</span>
                    </>
                  )}
                </span>
              </p>
              <p className="Food-week-row">
                <span className="Food-week-row-label">burn</span>
                <span className="Food-week-row-value">
                  {summary.burnTotal === null ? (
                    <span className="Food-undisclosed" title="No countable day in this window">&mdash;</span>
                  ) : (
                    <>
                      {Math.round(summary.burnTotal).toLocaleString('en-US')}
                      {summary.deficitIsFloor && (
                        <sup className="Food-partial" title={`${summary.unloggedCount} of 7 days unlogged`}>+</sup>
                      )}
                      <span className="Food-unit"> cal</span>
                      <span className="Food-week-row-sub">
                        {' '}· {Math.round(summary.burnBase).toLocaleString('en-US')} base
                        {' + '}{Math.round(summary.burnSwim).toLocaleString('en-US')} swim
                        {summary.swimMeters > 0 && ` (${summary.swimMeters.toLocaleString('en-US')} m)`}
                        {summary.burnWalk > 0 && ` + ${Math.round(summary.burnWalk).toLocaleString('en-US')} walk`}
                      </span>
                    </>
                  )}
                </span>
              </p>
            </div>

            {/* Predicted vs observed — the point of the whole view. Both are
                weight CHANGE over the window; the gap is stated as a plain
                percentage and nothing here says which one is right. */}
            <div className="Food-week-pvo">
              <p className="Food-week-row">
                <span className="Food-week-row-label">predicted</span>
                <span className="Food-week-row-value">
                  {predicted === null
                    ? <span className="Food-undisclosed">&mdash;</span>
                    : formatLb(predicted)}
                </span>
              </p>
              <p className="Food-week-row">
                <span className="Food-week-row-label">observed</span>
                <span className="Food-week-row-value">
                  {observed === null ? (
                    <>
                      <span className="Food-undisclosed" title="Fewer than 4 weigh-ins in this window">&mdash;</span>
                      <span className="Food-week-row-sub"> not enough readings</span>
                    </>
                  ) : (
                    formatLb(observed)
                  )}
                </span>
              </p>
              {gapPct !== null && (
                <p className="Food-week-gap">gap {gapPct}%</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Food() {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(toDateOnly(new Date()));
  const [adding, setAdding] = useState(false);
  // Bumped by any successful write. It is a dependency of the fetch effect, so
  // incrementing it re-reads the day from the server rather than patching
  // `rows` locally — which is deliberate: totalNutrients runs above the return
  // and iterates every row, so a hand-assembled row of the wrong shape would
  // throw during render and take the whole component down, day arrows included.
  // Everything in `rows` therefore comes from the range fetch below and
  // nowhere else.
  const [reloadKey, setReloadKey] = useState(0);
  // id of the row showing its delete confirmation, and the id currently being
  // deleted. Separate, because the confirm stays up while the request is in
  // flight so the button can read "Deleting…".
  const [confirmId, setConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // The day's burn picture, or null. Null is the same answer for "not loaded
  // yet", "no weigh-in on or before this day" and "the read failed" — all three
  // render the same dash, because in all three the honest statement is that this
  // view cannot say what was burned.
  const [burn, setBurn] = useState(null);
  // The two fitness arrays behind that burn, kept for the Past 7 days overlay
  // so it can compute seven per-day burns without a second fitnessApi fetch.
  const [fitnessData, setFitnessData] = useState(null);
  const [weekOpen, setWeekOpen] = useState(false);

  // The open detail popover: { key: 'Sodium' | ... | 'Calories', pinned }.
  // pinned marks click/tap-opened (dismissed explicitly or by tapping away)
  // vs hover-opened (dismissed by mouseleave). Touch only ever sets pinned.
  const [pop, setPop] = useState(null);
  // DOM nodes for the anchors, by key — callback refs into a plain object,
  // because the anchors mount and unmount with the day's branches.
  const popAnchors = useRef({});
  const anchorRef = key => el => { popAnchors.current[key] = el; };
  const togglePop = key => () =>
    setPop(p => (p !== null && p.key === key && p.pinned ? null : { key, pinned: true }));
  const hoverProps = key => (IS_TOUCH ? {} : {
    onMouseEnter: () => setPop(p => (p !== null && p.pinned ? p : { key, pinned: false })),
    onMouseLeave: () => setPop(p => (p !== null && p.key === key && !p.pinned ? null : p)),
  });
  const closePop = useCallback(() => setPop(null), []);

  // A popover is about the day on screen; changing the day closes it.
  useEffect(() => { setPop(null); }, [date]);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  // The day's rows are set by the range effect below — the viewed day is
  // always the window's last key, so a separate getFoodLogForDate call would
  // pull the identical full list a second time to filter out a subset the
  // range fetch already holds. One fetch serves the ledger, the protein card
  // and the popover run rates alike; the loading/error pair that used to
  // belong to a day-only fetch now belongs to that shared one.

  const userEmail = accounts[0]?.username || '';

  // Burn reads three OTHER lists and is deliberately NOT on reloadKey. Logging
  // a food changes what was eaten, never what was burned, and putting this on
  // the same counter would re-pull SwimmingWorkouts, FitnessWeighIns and
  // WalkingWorkouts on every single entry. It keys on the day and the user,
  // which are the only two things that change the answer.
  //
  // Both lists are per-user (CreatedByEmail), the same filter every fitness
  // screen applies.
  //
  // A failure here sets burn to null and goes no further: the day view owns the
  // food log, and a swim list that will not load must not take the ledger down
  // with it. The bar says it cannot answer; everything else still renders.
  useEffect(() => {
    let cancelled = false;
    setBurn(null);
    (async () => {
      try {
        const token = await getToken();
        const [workouts, weighIns, walks] = await Promise.all([
          getFitnessItems(token, 'SwimmingWorkouts'),
          getFitnessItems(token, 'FitnessWeighIns'),
          getFitnessItems(token, 'WalkingWorkouts'),
        ]);
        if (cancelled) return;
        const mine = row => row.CreatedByEmail === userEmail;
        const data = {
          weighIns: weighIns.filter(mine),
          workouts: workouts.filter(mine),
          walks: walks.filter(mine),
        };
        // The arrays outlive the single-day burn: the Past 7 days overlay
        // computes per-day burns from these same two fetches rather than
        // calling fitnessApi again (no pagination there, hard 500-row top).
        setFitnessData(data);
        setBurn(computeBurn({ ...data, dayKey: date }));
      } catch {
        if (!cancelled) { setBurn(null); setFitnessData(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, date, userEmail]);

  // The ONE Food_Log fetch per day view. The seven-day window ends on the
  // viewed day, so the day's rows ARE the window's last bucket — fetching
  // them separately through getFoodLogForDate pulled the identical full list
  // a second time (both helpers read the whole list and filter client-side)
  // to extract a subset this fetch already holds. rowsByDay[date] preserves
  // the helper's exact shape and ordering: both filters walk the same
  // getItems result in row order.
  //
  // This effect therefore owns loading and error for the day view too. A
  // failed range read sets `error` and renders the error state — it must
  // never fall through to an empty ledger, which would present a fetch
  // failure as a day on which nothing was eaten.
  //
  // Keyed on reloadKey so every successful write re-reads from the server
  // rather than patching `rows` locally, and on date because the window ends
  // on the viewed day. The arrows stay live during a fetch, so two clicks in
  // quick succession put two requests in flight and they can land out of
  // order; cleanup runs the moment `date` changes, the superseded request
  // sets `cancelled`, and every setState it would have made is dropped —
  // including setLoading. Only the most recently requested day writes state.
  const [proteinWindow, setProteinWindow] = useState(null);
  // The window's rows, kept so the mineral popovers can derive per-nutrient
  // run rates from the fetch that already ran. Null while loading or after a
  // failed read — the popover renders its dash for it.
  const [windowRows, setWindowRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProteinWindow(null);
    setWindowRows(null);
    (async () => {
      try {
        const token = await getToken();
        const startKey = shiftDay(date, -6);
        const rangeRows = await getFoodLogForRange(token, startKey, date);
        if (cancelled) return;
        const dayKeys = [];
        for (let i = 0; i < PROTEIN_WINDOW_DAYS; i++) dayKeys.push(shiftDay(startKey, i));
        const rowsByDay = groupRowsByDay(rangeRows, dayKeys);
        setRows(rowsByDay[date]);
        setProteinWindow(proteinRunRate({ dayKeys, rowsByDay }));
        setWindowRows({ dayKeys, rowsByDay });
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, date, reloadKey]);

  // Delete goes through the same door as the initial load: the row is removed
  // server-side, then reloadKey re-runs the fetch above. `rows` is never
  // spliced by hand, so this path cannot introduce a row shape totalNutrients
  // has not already survived once.
  async function handleDelete(id) {
    setDeletingId(id);
    setError(null);
    try {
      const token = await getToken();
      await deleteItem(token, 'Food_Log', id);
      setConfirmId(null);
      setReloadKey(k => k + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  }

  const today = toDateOnly(new Date());
  // Front-page columns plus the two mcg micros: IodineMcg and RetinolMcg are
  // tiles now, so their totals ride the same object the strip and the
  // popovers read — totals[key] must exist for every MINERALS key or the
  // tile render throws.
  const totals = totalNutrients(rows, [...NUTRIENTS, ...DETAIL_NUTRIENTS]);
  const estimated = estimatedCalories(rows);
  const ledger = sortByTime(rows);

  // A day with no rows and a day whose rows all withheld calories both read
  // null here, and both render the empty runway with an em-dash headline. The
  // previous version coerced the no-rows day to a disclosed zero ("nothing
  // eaten is zero eaten") — reversed deliberately: a no-food day rendering as
  // a 0-calorie day painted a full-width runway-to-burn that read as a
  // reading, on the day most likely to simply not have been logged yet.
  const intakeCal = totals.Calories.value;

  // The calorie target: the explicit intake goal when one is set, else the
  // day's computed maintenance — the same roundedBurnCal every other calorie
  // figure on screen reads. Null when neither exists (no weigh-in).
  const calorieTarget = CALORIE_TARGET_CAL !== null
    ? CALORIE_TARGET_CAL
    : (burn === null ? null : roundedBurnCal(burn));
  const calorieTargetLabel = CALORIE_TARGET_CAL !== null ? 'intake goal' : 'maintenance';
  const calStatus = calorieTarget === null
    ? { state: 'unknown', ratio: null }
    : nutrientStatus({
        value: intakeCal,
        undisclosed: totals.Calories.undisclosed,
        target: calorieTarget,
        direction: 'ceiling',
      });
  // The ring diverges from the tiles here, deliberately. statusColor()
  // returns null for unknown/incomplete and the tiles inherit the default
  // TEXT colour, which is neutral — but the ring's CSS default stroke is the
  // good green, so a null falls through to a verdict. Undisclosed calories
  // mean actual intake is HIGHER than shown: a day that looks under target
  // may be over it, and a green ring on that day is a false all-clear. The
  // ring and the kcal number resolve those states to the muted grey
  // explicitly; statusColor() itself keeps returning null, which the tiles
  // depend on.
  const ringColor = statusColor(calStatus.state) ?? 'var(--food-status-unresolved)';

  // The day's reveal clock — stamped at the render where a date's content
  // first appears (its rows fetch just landed). That is the ONLY stamping:
  // a save's refetch remounts the same date, finds the clock stale, and
  // renders settled. The ctx object is kept in the ref so consumers see one
  // stable identity per landing. Reduced-motion is read at the stamp: under
  // it every progress is born at 1 and the arrive class never applies.
  const revealClock = useRef({ date: null, ctx: { startTime: -Infinity, reduced: true } });
  if (!loading && revealClock.current.date !== date) {
    revealClock.current = {
      date,
      ctx: { startTime: performance.now(), reduced: prefersReducedMotion() },
    };
  }
  const revealArriving =
    !revealClock.current.ctx.reduced &&
    performance.now() - revealClock.current.ctx.startTime < REVEAL_TOTAL_MS;

  // No early return on `loading`. The header owns the date, not the fetch, so
  // unmounting it mid-fetch made every arrow click blank out the control the
  // user had just pressed. Only the region below it swaps.
  return (
    <div className="Food">
      <div className="Food-daynav">
        <button
          type="button"
          className="Food-daynav-arrow"
          aria-label="Previous day"
          onClick={() => setDate(d => shiftDay(d, -1))}
        >
          &lsaquo;
        </button>
        <h1 className="Food-title">{formatDayHeader(date)}</h1>
        <button
          type="button"
          className="Food-daynav-arrow"
          aria-label="Next day"
          onClick={() => setDate(d => shiftDay(d, 1))}
        >
          &rsaquo;
        </button>
      </div>

      {date !== today && (
        <button type="button" className="Food-today-btn" onClick={() => setDate(today)}>
          Today
        </button>
      )}

      {/* Outside every loading/rows.length conditional on purpose. The day you
          most need this button is the day with nothing on it, and gating it on
          rows.length > 0 would hide it precisely then. It also stays put during
          a fetch, for the same reason the day arrows do. */}
      <button
        type="button"
        className="Food-add-btn"
        onClick={() => setAdding(true)}
      >
        Add Food
      </button>

      {error && <p className="Food-error">{error}</p>}

      {loading && <p className="Food-loading">Loading food log...</p>}

      {/* One card holds the whole day — hero, macros, bar, minerals, ledger,
          weight row — so the numbers sit on a surface instead of floating on
          the page. The two content branches are mutually exclusive on
          rows.length; the card and the weight row are common to both. */}
      {/* The keyed wrapper is the reveal's boundary: display: contents, so it
          is invisible to layout, and keyed on the date so paging remounts the
          content — landing on a day and measuring it in — while a same-date
          remount (a save's refetch) reads the stale clock and renders
          settled. The nav, header and Add Food button live outside it and
          never move. */}
      {!loading && (
      <RevealContext.Provider value={revealClock.current.ctx}>
      <div className={`Food-day${revealArriving ? ' Food-day--arrive' : ''}`} key={date}>
      <div className="Food-card">
      {rows.length === 0 && (
        <>
          {/* The hero row and bar on a day with nothing logged: burn does not
              depend on food, and the day you ate nothing is the day the
              reading is most interesting. The intake side is the em dash it
              honestly is — undisclosed, not zero — and the ring around it
              stays unfilled. */}
          <div className="Food-hero">
            <div className="Food-hero-left">
              <FoodRing
                burn={burn}
                intakeCal={intakeCal}
                ringColor={ringColor}
                anchorRef={anchorRef('Calories')}
                onToggle={togglePop('Calories')}
                popoverOpen={pop !== null && pop.key === 'Calories'}
                hover={hoverProps('Calories')}
              >
                <p className="Food-kcal">
                  <span className="Food-kcal-value"><Amount value={null} /></span>
                  <span className="Food-kcal-unit"> cal</span>
                </p>
              </FoodRing>
            </div>
            <div className="Food-hero-centre">
              <HeroCentre burn={burn} intakeCal={intakeCal} />
            </div>
            <div className="Food-hero-right">
              <BurnTile burn={burn} />
            </div>
          </div>
          <BurnBar burn={burn} intakeCal={intakeCal} />
          <p className="Food-empty">Nothing logged for this day.</p>
        </>
      )}

      {rows.length > 0 && (
        <>
          {/* The headline number takes the same three states as every other
              total: a day whose calories are half-sourced says so on the
              number itself. The est. suffix below is a separate axis and the
              two are independent — the marker answers "is this total
              complete?", the suffix answers "how much of it was guessed at?".
              A day can be fully disclosed and entirely estimated, or complete
              on every catalog row and missing one hand-typed entry. */}
          <div className="Food-hero">
            <div className="Food-hero-left">
              <FoodRing
                burn={burn}
                intakeCal={intakeCal}
                ringColor={ringColor}
                anchorRef={anchorRef('Calories')}
                onToggle={togglePop('Calories')}
                popoverOpen={pop !== null && pop.key === 'Calories'}
                hover={hoverProps('Calories')}
              >
          <p className="Food-kcal">
            <span className="Food-kcal-value" style={{ color: ringColor }}>
              <Amount value={totals.Calories.value} undisclosed={totals.Calories.undisclosed} count={SEGMENTS.ring} />
            </span>
            <span className="Food-kcal-unit"> cal</span>
            {/* The suffix qualifies the calorie number to its left, so it only
                appears when it has something to say about that number. Two
                cases where it does not:

                  null  — no non-catalog row disclosed calories at all
                  zero  — estimated rows exist but contribute no calories

                The zero case is real and reachable: log an LMNT stick and the
                day has an estimated row worth 0 kcal, which rendered as
                "170 cal · 0 est." — true, and useless. It is suppressed on the
                ROUNDED value, so a total that displays as 0 never appears
                either. Nothing is lost by hiding it: those rows still carry
                their own EST badge in the ledger below, which is where a row
                that contributes no calories belongs. */}
            {estimated !== null && Math.round(estimated) !== 0 && (
              <span className="Food-kcal-est">
                {' · '}{Math.round(estimated).toLocaleString('en-US')} est.
              </span>
            )}
          </p>
              </FoodRing>

          {/* Macros and minerals both carry the partial marker. A day's protein
              is as capable of being half-sourced as its magnesium, and the
              caveat belongs on whichever number it applies to. */}
          <p className="Food-macros">
            <span className="Food-macro">Protein <Amount value={totals.Protein.value} undisclosed={totals.Protein.undisclosed} unit="g" /></span>
            <span className="Food-macro">Fat <Amount value={totals.Fat.value} undisclosed={totals.Fat.undisclosed} unit="g" /></span>
            <span className="Food-macro">Carbs <Amount value={totals.Carbs.value} undisclosed={totals.Carbs.undisclosed} unit="g" /></span>
          </p>
            </div>

            <div className="Food-hero-centre">
              <HeroCentre burn={burn} intakeCal={intakeCal} />
            </div>

            <div className="Food-hero-right">
              <BurnTile burn={burn} />
            </div>
          </div>

          {/* Under the hero row, above the mineral strip, same width as both —
              the bar is the hero's geometry restated at full width. */}
          <BurnBar burn={burn} intakeCal={intakeCal} />

          <div className="Food-minerals">
            {MINERALS.map(({ key, label, target, unit, direction }, tileIndex) => {
              /* The word is the label — the column key IS the element's name,
                 so Sodium/Potassium/Magnesium/Calcium come straight off it.
                 The tile is a button now: tap opens the target detail. The
                 value's colour is the status — and stays the default text
                 colour on unknown/incomplete, which are never colour-judged. */
              const { state } = nutrientStatus({
                value: totals[key].value,
                undisclosed: totals[key].undisclosed,
                target,
                direction,
              });
              const color = statusColor(state);
              return (
                <button
                  type="button"
                  className="Food-mineral"
                  key={key}
                  data-food-pop=""
                  ref={anchorRef(key)}
                  onClick={togglePop(key)}
                  aria-expanded={pop !== null && pop.key === key}
                  style={{ animationDelay: `${mineralRevealDelayMs(tileIndex)}ms` }}
                  {...hoverProps(key)}
                >
                  <span className="Food-mineral-symbol">{label}</span>
                  <span className="Food-mineral-value" style={color ? { color } : undefined}>
                    <Amount
                      value={totals[key].value}
                      undisclosed={totals[key].undisclosed}
                      unit={unit}
                      count={mineralCountSegment(tileIndex)}
                    />
                  </span>
                </button>
              );
            })}
          </div>

        </>
      )}
      </div>

      {/* Below the macro/mineral card, above the ledger — present on empty
          days too, which is when its denominator rule matters most. */}
      <ProteinRunRateCard data={proteinWindow} />

      {/* The ledger in its own card, separate from the hero card. The day, in
          the order it was eaten. One list, no headings: the rows are already
          sorted, and a heading would have to name a grouping that no longer
          exists. */}
      {rows.length > 0 && (
          <section className="Food-card Food-card--ledger Food-log">
            <ul className="Food-log-list">
              {ledger.map((entry, rowIndex) => {
                const time = fromTimeOnly(entry.Time);
                const confirming = confirmId === entry.id;
                return (
                  <li
                    className="Food-entry-item"
                    key={entry.id}
                    style={{ animationDelay: `${rowRevealDelayMs(rowIndex)}ms` }}
                  >
                    {confirming ? (
                      /* Two-step by design. This list sits under a thumb on a
                         phone, and a single tap must never destroy a row. */
                      <div className="Food-entry-confirm">
                        <span className="Food-entry-confirm-text">
                          Delete {entry.Title} &middot; {formatQuantity(entry)}?
                        </span>
                        <span className="Food-entry-confirm-actions">
                          <button
                            type="button"
                            className="Food-entry-cancel"
                            onClick={() => setConfirmId(null)}
                            disabled={deletingId === entry.id}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="Food-entry-delete"
                            onClick={() => handleDelete(entry.id)}
                            disabled={deletingId === entry.id}
                          >
                            {deletingId === entry.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="Food-entry"
                        onClick={() => setConfirmId(entry.id)}
                      >
                        {/* Rendered verbatim. A row whose time is missing or was
                            written past the guard gets the same em dash an
                            undisclosed nutrient gets — absent, not zero, and not
                            guessed at from the row's position in the list. */}
                        <span className="Food-entry-time">
                          {time || <span className="Food-undisclosed" title="No time recorded">&mdash;</span>}
                        </span>
                        <span className="Food-entry-name">{entry.Title}</span>
                        <span className="Food-entry-qty">{formatQuantity(entry)}</span>
                        <span className="Food-entry-kcal">
                          <Amount value={entry.Calories} unit="cal" />
                        </span>
                        {entry.EntrySource !== 'Catalog' && (
                          <span
                            className="Food-entry-est"
                            title={`Logged as ${entry.EntrySource || 'unspecified'}`}
                          >
                            est
                          </span>
                        )}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
      )}

      {/* Below the ledger on a populated day, below the hero card otherwise —
          one instance, common to both branches, so the row does not remount
          when the day flips between them. */}
      <WeightRow burn={burn} dayKey={date} onOpenWeek={() => setWeekOpen(true)} />
      </div>
      </RevealContext.Provider>
      )}

      <WeekSheet
        open={weekOpen}
        onClose={() => setWeekOpen(false)}
        getToken={getToken}
        fitnessData={fitnessData}
      />

      {/* One popover for every anchor — ring and tiles. Content is chosen by
          key; FoodPopover owns position and dismissal. */}
      <FoodPopover
        anchorEl={pop === null ? null : popAnchors.current[pop.key]}
        open={pop !== null}
        onClose={closePop}
        estHeight={pop !== null && pop.key === 'Calories' ? 280 : 190}
        ariaLabel={pop === null ? undefined : (pop.key === 'Calories' ? 'Calorie detail' : `${NUTRIENT_TARGETS[pop.key].label} detail`)}
      >
        {pop !== null && pop.key === 'Calories' && (
          <CaloriePopover
            intake={totals.Calories}
            target={calorieTarget}
            targetLabel={calorieTargetLabel}
            state={calStatus.state}
          />
        )}
        {pop !== null && pop.key !== 'Calories' && (
          <MineralPopover
            targetDef={NUTRIENT_TARGETS[pop.key]}
            total={totals[pop.key]}
            dayKeys={windowRows === null ? null : windowRows.dayKeys}
            rowsByDay={windowRows === null ? null : windowRows.rowsByDay}
          />
        )}
      </FoodPopover>

      {/* Sibling of the content, not nested inside the rows.length fragment, so
          it survives the empty/populated flip and stays mounted across several
          logs. onLogged only bumps the reload counter — the sheet keeps itself
          open and returns to its picker. */}
      <AddFoodSheet
        open={adding}
        dateStr={date}
        getToken={getToken}
        onClose={() => setAdding(false)}
        onLogged={() => setReloadKey(k => k + 1)}
      />
    </div>
  );
}

export default Food;
